import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ItemsDtoResponse } from '@app/cem/project/services/item-management.service';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { IItemScopingCategory } from '@app/shared/interfaces/iitem-scoping-category';
import { ICapacityCategoryDto } from '@app/shared/models/dtos/capacity-category';
import { ICapacitySubCategoryDto } from '@app/shared/models/dtos/capacity-sub-category';
import { IItemDto } from '@app/shared/models/dtos/item';
import { IProjectTankBatteryScopingItemDto } from '@app/shared/models/dtos/project-tank-battery-scoping-item';
import { ReplaySubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class ProjectScopingService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  private BASE_URL = '';

  public editedRecords: Map<number, IProjectTankBatteryScopingItemDto> = new Map<any, IProjectTankBatteryScopingItemDto>();
  public removedRecords: Map<number, IProjectTankBatteryScopingItemDto> = new Map<any, IProjectTankBatteryScopingItemDto>();
  public newProjectTankBatteryScopingItemId: number = 0;

  public IItemDtos: Array<IItemDto>;
  public ActiveIItemDtos: Array<IItemDto>;

  public IItemScopingCategories: Array<IItemScopingCategory>;
  public IProjectTankBatteryScopingItemDtos: Array<IProjectTankBatteryScopingItemDto>;

  public LoadingEvent: ReplaySubject<boolean>;

  constructor() {
    this.LoadingEvent = new ReplaySubject();
    this.BASE_URL = this.environment.apiUrl;
  }

  public async loadData(projectId: number) {
    this.LoadingEvent.next(true);

    try {
      this.IItemDtos = (await this.findItemDtosForProjectScopingAsync(projectId)).Results;
      this.IItemDtos.forEach(e => {
        if (e.IsActive === false) {
          e.Name += " (Inactive)"
        }
      });
      this.IItemScopingCategories = await this.loadItemScopingCategories();
      this.IProjectTankBatteryScopingItemDtos = (await this.findProjectScopingItems(projectId)).Results;
    }
    catch (err) {
      this.errorService.handleHTTPError(err)
    }
    finally {
      this.LoadingEvent.next(false);
    }
  }

  public async findProjectScopingItems(projectId: number): Promise<ProjectTankBatteryScopingItemDtoResponse> {
    let url = `${this.BASE_URL}scopingitem/FindProjectScopingItems?projectId=${projectId}`;
    return this.http.get<ProjectTankBatteryScopingItemDtoResponse>(url).toPromise();
  }

  public async findItemDtosForProjectScopingAsync(projectId: number): Promise<ItemsDtoResponse> {
    let url = `${this.BASE_URL}scopingitem/FindItemDtosForProjectScopingAsync?projectId=${projectId}`;
    return this.http.get<ItemsDtoResponse>(url).toPromise();
  }

  public async findProjectCapacityCategoryDtosAsync(projectId: number): Promise<CapacitySubCategoryDtoResponse> {
    let url = `${this.BASE_URL}scopingitem/FindProjectCapacitySubCategoryDtosAsync?projectId=${projectId}`;
    return this.http.get<CapacitySubCategoryDtoResponse>(url).toPromise();
  }

  public getDailyAggregateForecastScenarioData(projectId: number, scenarioId: number): Promise<CapacitySubCategoryDtoResponse> {
    let url = `${this.BASE_URL}scopingitem/GetDailyAggregateForecastScenarioData?projectId=${projectId}&scenarioId=${scenarioId}`;
    return this.http.get<any>(url).toPromise();
  }

  public async calculateAndSaveProjectCapacities(projectId: number): Promise<any> {
    let url = `${this.BASE_URL}scopingitem/CalculateAndSaveProjectCapacities`;
    return this.http.post<any>(url, projectId).pipe(
      catchError(e => this.errorService.handleHTTPError(e))
    ).toPromise();
  }

  public getScopingCapitalData(projectId: number, scenarioId: number): Promise<IScopingCapitalDataResponse> {
    let url = `${this.BASE_URL}scopingitem/GetScopingCapitalData?projectId=${projectId}&scenarioId=${scenarioId}`;
    return this.http.get<any>(url).toPromise();
  }

  public async loadItemScopingCategories(): Promise<Array<IItemScopingCategory>> {
    let url = `${this.BASE_URL}ItemScopingCategories?$select=ItemScopingCategoryId,Description,SortOrder`;
    return this.http.get<any>(url).pipe(
      map(m => m.value)
    ).toPromise();
  }

  public async saveChanges(data: IProjectScopingItemSaveRequest): Promise<any> {
    let url = `${this.BASE_URL}scopingitem/UpsertProjectScopingItems`;
    return this.http.post<any>(url, data).pipe(
      catchError(e => this.errorService.handleHTTPError(e))
    ).toPromise();
  }

  //used for both save and delete of a note
  public async saveNote(data: IProjectScopingItemNoteSaveRequest): Promise<IProjectScopingItemNoteSaveRequest> {
    let url = `${this.BASE_URL}scopingitem/UpsertProjectScopingItemNote`;
    return this.http.post<IProjectScopingItemNoteSaveRequest>(url, data).pipe(
      catchError(
        e => {
          return this.errorService.handleHTTPError(e)
        })
    ).toPromise();
  }

  public upsertRecord(id: number, item: IProjectTankBatteryScopingItemDto) {
    this.editedRecords.set(id, item);
    this.removedRecords.delete(id);
  }

  public removeRecord(id: number, item: IProjectTankBatteryScopingItemDto) {
    this.editedRecords.delete(id);
    this.removedRecords.set(id, item);
  }

  public resetChanges() {
    this.editedRecords = new Map<any, IProjectTankBatteryScopingItemDto>();
    this.removedRecords = new Map<any, IProjectTankBatteryScopingItemDto>();

    this.newProjectTankBatteryScopingItemId = 0;
  }
}

export interface ProjectTankBatteryScopingItemDtoResponse {
  Results: Array<IProjectTankBatteryScopingItemDto>;
}

export interface CapacityCategoryDtoResponse {
  Results: Array<ICapacityCategoryDto>;
}

export interface CapacitySubCategoryDtoResponse {
  Results: Array<ICapacitySubCategoryDto>;
}

export interface IProjectScopingItemSaveRequest {
  ProjectId: number;
  Upserts: Array<IProjectTankBatteryScopingItemDto>;
  Removals: Array<IProjectTankBatteryScopingItemDto>;
}

export interface IProjectScopingItemNoteSaveRequest {
  ProjectTankBatteryScopingItemId: number;
  Note: string;
}

export interface IScopingCapitalDataResponse {
  EstimateTotal: number;
  CostPerWell: number;
  CostPerLL: number;
  CostPerCum365BO: number;
}